import { create } from 'zustand';

/**
 * Handle that a mounted composer registers so the Bizy UI block
 * dispatcher can act on it from a chat-card click — without having to
 * thread closures through `pendingContextualInsert` (which gets
 * cleared on panel close and breaks the second click).
 */
export interface BizyCommentComposerHandle {
	/**
	 * Insert AI-drafted text into the composer with the AI-touched
	 * highlight, scrolling the composer into view first if it's offscreen.
	 * Idempotent — calling repeatedly always re-inserts the latest text.
	 */
	insertDraft: (text: string) => void;
	/**
	 * Insert + submit the comment immediately via the composer's normal
	 * submit path (validation, mutation, cache update). Returns whether
	 * the submit was actually attempted (false if the composer wasn't
	 * mountable). Idempotent in the sense that repeated calls always
	 * try a fresh submit; the composer's onSubmit handles its own
	 * loading / dedupe state.
	 */
	sendNow: (text: string) => boolean;
	/**
	 * Bring the composer back into the viewport (e.g. user scrolled
	 * away while chatting with Bizy in the side panel) and focus the
	 * editor. Called after panel close even when no insert / send
	 * happened, so the user lands back on the composer they invoked
	 * Bizy from.
	 */
	scrollIntoViewAndFocus: () => void;
}

interface BizyActiveComposersStore {
	/**
	 * Map of bonusId → composer handle. Multiple comment composers can
	 * be mounted at once (one per bonus rendered in the feed); each
	 * registers itself on mount and unregisters on unmount.
	 */
	commentComposers: Map<string, BizyCommentComposerHandle>;

	/**
	 * Map of bonusId → queued draft text waiting for a composer to
	 * mount. The dispatcher writes here when Insert-draft fires for a
	 * bonus whose composer isn't currently mounted (user navigated
	 * away from the parent post mid-`/`-session); the dispatcher then
	 * navigates to `/bonuses/{bonusId}`, the bonus-modal route mounts
	 * the comment composer, and `registerCommentComposer` immediately
	 * drains this queue + clears it. Only one draft per bonus — a
	 * second click overwrites the first (latest intent wins).
	 *
	 * NOT a shadow registry of "every composer that ever wanted text" —
	 * we drain on first mount and forget. Persisting beyond the next
	 * mount would resurrect stale drafts on unrelated future visits.
	 */
	pendingInserts: Map<string, string>;

	/**
	 * Register a comment composer. Returns an unregister fn the caller
	 * should run on unmount (e.g. inside a useEffect cleanup).
	 *
	 * If a composer with the same bonusId is already registered the new
	 * one wins — this matches "the most recently mounted composer for
	 * this bonus is the one that should receive Bizy actions" and
	 * handles strict-mode double-mount cleanly.
	 *
	 * Drains the matching `pendingInserts` entry as the last step of
	 * registration so the queue+navigate fallback in the UI block
	 * dispatcher lands the draft as soon as the composer can accept
	 * it. Drained entries are removed from the queue (no replay).
	 */
	registerCommentComposer: (bonusId: string, handle: BizyCommentComposerHandle) => () => void;

	/**
	 * Look up the composer for a bonus id. Returns undefined when no
	 * composer is currently mounted (user closed the parent post,
	 * navigated away, etc.) so callers can decide on a fallback.
	 */
	getCommentComposer: (bonusId: string) => BizyCommentComposerHandle | undefined;

	/**
	 * Queue a draft for a bonus whose composer isn't mounted yet.
	 * Caller is responsible for separately driving the surface that
	 * causes the composer to mount (e.g. navigating to the bonus
	 * URL); this store is just the pickup point for the eventual
	 * mount-time drain.
	 */
	queuePendingInsert: (bonusId: string, text: string) => void;
}

/**
 * Why a Zustand store and not a React context: the Bizy chat view and
 * the comment composers live in different subtrees of the React tree
 * (chat lives in the side panel; composers live in the feed). A
 * context provider above them would have to wrap the entire app and
 * re-render on every register/unregister. A Zustand store is a flat
 * shared registry both subtrees can read/write without any wrapping.
 */
export const useBizyActiveComposersStore = create<BizyActiveComposersStore>((set, get) => ({
	commentComposers: new Map(),
	pendingInserts: new Map(),
	registerCommentComposer: (bonusId, handle) => {
		// Snapshot any pending insert BEFORE we mutate the store, so the
		// drain happens against the same value we're about to clear.
		// Avoids a race where a parallel queuePendingInsert inserts a
		// new draft between our drain and the clear (the new draft
		// would be silently dropped).
		const queued = get().pendingInserts.get(bonusId);
		set((state) => {
			const nextComposers = new Map(state.commentComposers);
			nextComposers.set(bonusId, handle);
			if (!queued) return { commentComposers: nextComposers };
			const nextPending = new Map(state.pendingInserts);
			nextPending.delete(bonusId);
			return { commentComposers: nextComposers, pendingInserts: nextPending };
		});
		// Apply the drained draft AFTER the registration commit so the
		// composer's `insertDraft` (which reads from refs at call time)
		// sees a fully-registered handle. The handle itself defers to
		// rAF when the editor isn't ready yet, so this is safe to fire
		// synchronously here.
		if (queued) handle.insertDraft(queued);

		return () => {
			set((state) => {
				// Only delete if THIS handle is still registered — defends
				// against strict-mode double-mount where the new mount's
				// register fires before the old mount's unregister.
				const current = state.commentComposers.get(bonusId);
				if (current !== handle) return state;
				const next = new Map(state.commentComposers);
				next.delete(bonusId);
				return { commentComposers: next };
			});
		};
	},
	getCommentComposer: (bonusId) => get().commentComposers.get(bonusId),
	queuePendingInsert: (bonusId, text) => {
		set((state) => {
			const next = new Map(state.pendingInserts);
			next.set(bonusId, text);
			return { pendingInserts: next };
		});
	},
}));
